import {
  Avatar, Box, Button, Flex, HStack, Icon, Input, Progress, SimpleGrid, Spinner, Text, useToast, VStack
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  PiArrowLeftBold, PiChartBarDuotone, PiCalendarBlankDuotone, PiCheckCircleDuotone, PiClockDuotone,
  PiArrowSquareOutDuotone, PiEyeDuotone, PiGlobeDuotone, PiListChecksDuotone, PiNotePencilDuotone, PiLockDuotone,
  PiBroadcastDuotone, PiMagnifyingGlassDuotone, PiShieldCheckDuotone, PiTrendUpDuotone, PiUserPlusDuotone,
  PiUsersThreeDuotone, PiXCircleDuotone, PiLightningDuotone, PiUsersFourDuotone,
  PiWarningCircleDuotone, PiTrophyDuotone, PiListNumbersDuotone, PiChecksDuotone, PiHourglassMediumDuotone
} from "react-icons/pi";
import {
  getVotingDetails,
  getVotingResults,
  getVotingVoters,
  addVotingVoters,
  VotingResults,
  VotingParticipants,
  ResultOutcome,
  VOTING_STATUSES,
  convertHtmlToText
} from "@lubbezposrednio-frontend/core";
import { GLASS, SCROLL_FOG, drgba, EASE } from "../../components/glassUi";
import { BlockchainInfo } from "../VotingView/components/BlockchainInfo";

const STATUS_META: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  ongoing: { label: "Trwa", color: "#1f9e18", icon: PiBroadcastDuotone },
  planned: { label: "Planowane", color: "#7b2ff7", icon: PiClockDuotone },
  finished: { label: "Zakończone", color: "#2563eb", icon: PiCheckCircleDuotone },
  draft: { label: "Robocze", color: "#8a8397", icon: PiNotePencilDuotone }
};
const ELECTORATE: Record<string, { label: string; icon: React.ElementType }> = {
  closed: { label: "Zamknięta lista", icon: PiUsersFourDuotone },
  signup: { label: "Zapisy (vox populi)", icon: PiUserPlusDuotone },
  open: { label: "Referendum (otwarte)", icon: PiGlobeDuotone }
};

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const countdown = (iso?: string) => {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const min = Math.floor((diff % 3600000) / 60000);
  if (d >= 1) return `${d} dn. ${h} godz.`;
  if (h >= 1) return `${h} godz. ${min} min`;
  return `${min} min`;
};

const outcomeLabel = (o: ResultOutcome): { t: string; c: string } => {
  switch (o.kind) {
    case "passed":
      return { t: "Przyjęto", c: "#1f9e18" };
    case "rejected":
      return { t: "Odrzucono", c: "#e51f44" };
    case "winner":
      return { t: `Zwycięzca: ${o.winner}`, c: "#1f9e18" };
    case "passed_options":
      return { t: `Przyjęte: ${(o.passed ?? []).join(", ") || "brak"}`, c: "#1f9e18" };
    case "ranking":
      return { t: "Ranking", c: "#2563eb" };
    default:
      return { t: `Brak rozstrzygnięcia${o.reason ? ` (${o.reason})` : ""}`, c: "#b7791f" };
  }
};

// ── Prymitywy raila (styl pulpitu) ──
const RailHeading: React.FC<{ icon: React.ElementType; title: string; color?: string }> = ({ icon, title, color = "#430098" }) => (
  <HStack spacing="8px" mb="11px" minW={0}>
    <Icon as={icon} boxSize="18px" color={color} flexShrink={0} />
    <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.08em" noOfLines={1}>{title}</Text>
  </HStack>
);
const RailDivider = () => <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />;
const MetaRow: React.FC<{ icon: React.ElementType; color: string; label: string; value: React.ReactNode }> = ({ icon, color, label, value }) => (
  <HStack justify="space-between" spacing="10px" align="flex-start">
    <HStack spacing="8px" color="#6b6480" minW={0}><Icon as={icon} boxSize="14px" color={color} flexShrink={0} mt="1px" /><Text fontSize="13px" noOfLines={1}>{label}</Text></HStack>
    <Text fontSize="13px" fontWeight="800" color="#241536" textAlign="right" flexShrink={0} maxW="55%">{value}</Text>
  </HStack>
);

const StatTile: React.FC<{ icon: React.ElementType; value: React.ReactNode; label: string; color: string }> = ({ icon, value, label, color }) => (
  <Flex direction="column" borderRadius="16px" p="16px" gap="10px" bg="rgba(24,12,48,0.028)">
    <Flex w="38px" h="38px" borderRadius="11px" bg={drgba(color, 0.12)} align="center" justify="center">
      <Icon as={icon} boxSize="19px" color={color} />
    </Flex>
    <Box>
      <Text fontSize="26px" fontWeight="900" color="#1b1030" lineHeight="1.05" letterSpacing="-0.02em">{value}</Text>
      <Text fontSize="13px" color="#6b6480" fontWeight="600">{label}</Text>
    </Box>
  </Flex>
);

const emailInitials = (email: string) => {
  const local = (email.split("@")[0] || email).replace(/[^a-zA-Z0-9]/g, "");
  return (local.slice(0, 2) || email.slice(0, 2) || "?").toUpperCase();
};

const OUTCOME_ICON: Record<string, React.ElementType> = {
  passed: PiCheckCircleDuotone,
  rejected: PiXCircleDuotone,
  winner: PiTrophyDuotone,
  passed_options: PiChecksDuotone,
  ranking: PiListNumbersDuotone,
  none: PiWarningCircleDuotone
};

type DetailVoting = {
  title?: string;
  status?: string;
  public?: boolean;
  secret?: boolean;
  electorate_type?: string;
  quorum_percent?: number | null;
  requires_verification?: boolean;
  start_date?: string;
  end_date?: string;
  signup_landing_id?: string | null;
};

type ManageTab = "uprawnieni" | "wyniki";

const VotingManageView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [voting, setVoting] = useState<DetailVoting | null>(null);
  const [results, setResults] = useState<VotingResults | null>(null);
  const [participants, setParticipants] = useState<VotingParticipants | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addEmails, setAddEmails] = useState("");
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState<ManageTab>("uprawnieni");
  const toast = useToast();

  const loadVoters = useCallback(() => {
    if (id) getVotingVoters(id).then(r => setParticipants(r.data)).catch(() => undefined);
  }, [id]);

  const handleAdd = async () => {
    const emails = addEmails.split(/[\s,;]+/).map(e => e.trim()).filter(Boolean);
    if (!emails.length || !id) return;
    setAdding(true);
    try {
      const res = await addVotingVoters(id, emails);
      toast({ status: "success", isClosable: true, title: `Dodano ${res.data.added} ${res.data.added === 1 ? "osobę" : "osób"}` });
      setAddEmails("");
      loadVoters();
    } catch {
      toast({ status: "error", isClosable: true, title: "Nie udało się dopisać uprawnionych" });
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    document.title = "Zarządzanie głosowaniem";
    if (!id) return;
    Promise.all([getVotingDetails(id), getVotingResults(id).catch(() => null), getVotingVoters(id).catch(() => null)])
      .then(([detailsRes, resultsRes, votersRes]) => {
        setVoting((detailsRes?.data as { voting?: DetailVoting })?.voting ?? null);
        setResults(resultsRes?.data ?? null);
        setParticipants(votersRes?.data ?? null);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [id]);

  const status = voting?.status ?? "";
  const statusMeta = STATUS_META[status] ?? STATUS_META.draft;
  const electorate = ELECTORATE[voting?.electorate_type ?? "closed"] ?? ELECTORATE.closed;
  const isFinished = status === VOTING_STATUSES.FINISHED;
  const isOngoing = status === VOTING_STATUSES.ONGOING;
  const canAddVoters = (status === VOTING_STATUSES.PLANNED || status === VOTING_STATUSES.DRAFT) && voting?.electorate_type !== "open";
  const endsIn = isOngoing ? countdown(voting?.end_date) : null;

  const useVoters = (participants?.voters.length ?? 0) > 0 || (participants?.leads.length ?? 0) === 0;
  const rows = useMemo(() => {
    if (!participants) return [];
    const list = useVoters
      ? participants.voters.map(v => ({ email: v.email, ok: !!v.voted, okLabel: "Zagłosował", noLabel: "Nie zagłosował" }))
      : participants.leads.map(l => ({ email: l.email, ok: l.confirmed, okLabel: "Potwierdzony", noLabel: "Oczekuje" }));
    return list.filter(r => r.email.toLowerCase().includes(search.toLowerCase()));
  }, [participants, useVoters, search]);

  const revealParticipation = participants?.reveal_participation ?? isFinished;
  const showStatus = !useVoters || revealParticipation;
  const turnout = results?.turnout_percent ?? null;
  const isOpenElectorate = electorate.label === ELECTORATE.open.label;

  // ── Rail: konfiguracja + termin + akcje ──
  const rail = voting && (
    <>
      <HStack as={Link} to="/organizator/glosowania?w=autorskie" spacing="8px" color="#6b6480" fontSize="13px" fontWeight="700" mb="16px" _hover={{ color: "primary" }} w="fit-content">
        <Icon as={PiArrowLeftBold} boxSize="13px" />
        <Text>Twoje głosowania</Text>
      </HStack>

      <RailHeading icon={PiListChecksDuotone} title="Konfiguracja" color="#2563eb" />
      <VStack align="stretch" spacing="11px">
        <MetaRow icon={PiEyeDuotone} color="#2563eb" label="Widoczność" value={voting.public ? "Publiczne" : "Niepubliczne"} />
        <MetaRow icon={PiLockDuotone} color="#7b2ff7" label="Sposób głosu" value={voting.secret ? "Tajne" : "Jawne imienne"} />
        <MetaRow icon={electorate.icon} color="#0d9488" label="Elektorat" value={<Text noOfLines={2} textAlign="right">{electorate.label}</Text>} />
        <MetaRow icon={PiShieldCheckDuotone} color="#1f9e18" label="Weryfikacja" value={voting.requires_verification ? "Wymagana" : "Niewymagana"} />
        {voting.quorum_percent != null && <MetaRow icon={PiChartBarDuotone} color="#b7791f" label="Kworum" value={`${voting.quorum_percent}%`} />}
      </VStack>

      <RailDivider />

      <RailHeading icon={PiCalendarBlankDuotone} title="Termin" color="#7b2ff7" />
      <VStack align="stretch" spacing="11px">
        <MetaRow icon={PiCalendarBlankDuotone} color="#7b2ff7" label="Start" value={<Text fontSize="12.5px">{formatDate(voting.start_date)}</Text>} />
        <MetaRow icon={PiCalendarBlankDuotone} color="#7b2ff7" label="Koniec" value={<Text fontSize="12.5px">{formatDate(voting.end_date)}</Text>} />
        {endsIn && <MetaRow icon={PiClockDuotone} color="#e51f44" label="Kończy się za" value={endsIn} />}
      </VStack>

      <RailDivider />

      <RailHeading icon={PiLightningDuotone} title="Akcje" color="#e51f44" />
      <VStack align="stretch" spacing="8px">
        <Button as={Link} to={`/organizator/glosowanie/${id}/wyniki`} leftIcon={<Icon as={PiChartBarDuotone} />} bg="primary" color="white" borderRadius="12px" fontWeight="800" size="sm" _hover={{ bg: "#360079", textDecoration: "none" }}>
          Pełne wyniki
        </Button>
        {voting.electorate_type === "signup" && voting.signup_landing_id && (
          <Button as={Link} to={`/glosowanie/${id}`} leftIcon={<Icon as={PiUserPlusDuotone} />} variant="outline" borderColor="rgba(124,58,237,0.35)" color="#7c3aed" borderRadius="12px" fontWeight="800" size="sm" _hover={{ bg: drgba("#7c3aed", 0.06), textDecoration: "none" }}>
            Strona zapisów
          </Button>
        )}
        <Button as={Link} to={`/glosowanie/${id}`} leftIcon={<Icon as={PiArrowSquareOutDuotone} />} variant="outline" borderColor="rgba(67,0,152,0.25)" color="primary" borderRadius="12px" fontWeight="800" size="sm" _hover={{ bg: drgba("#430098", 0.05), textDecoration: "none" }}>
          Podgląd publiczny
        </Button>
      </VStack>
    </>
  );

  const main = voting && (
    <VStack align="stretch" spacing="16px">
      {/* ── HERO ── */}
      <Box borderRadius="24px" sx={GLASS} p={{ base: "20px", md: "26px" }} position="relative" overflow="hidden">
        <Box position="absolute" top="-90px" right="-70px" w="280px" h="280px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba(statusMeta.color, 0.12)}, transparent 70%)` }} />
        <Flex gap="16px" align="flex-start" direction={{ base: "column", sm: "row" }} position="relative">
          <Flex w={{ base: "50px", md: "56px" }} h={{ base: "50px", md: "56px" }} borderRadius="16px" bg={drgba(statusMeta.color, 0.12)} align="center" justify="center" flexShrink={0}>
            <Icon as={statusMeta.icon} boxSize="27px" color={statusMeta.color} />
          </Flex>
          <Box flex="1" minW={0}>
            <HStack spacing="9px" mb="7px" wrap="wrap">
              <HStack spacing="6px" bg={drgba(statusMeta.color, 0.12)} color={statusMeta.color} px="10px" py="4px" borderRadius="full">
                <Icon as={statusMeta.icon} boxSize="13px" />
                <Text fontSize="12.5px" fontWeight="800">{statusMeta.label}</Text>
              </HStack>
              {endsIn && (
                <HStack spacing="6px" bg="rgba(229,31,68,0.10)" color="accent" px="10px" py="4px" borderRadius="full">
                  <Icon as={PiClockDuotone} boxSize="13px" />
                  <Text fontSize="12.5px" fontWeight="800">Kończy się za {endsIn}</Text>
                </HStack>
              )}
              {turnout != null && (
                <HStack spacing="6px" bg={drgba("#7b2ff7", 0.1)} color="#7b2ff7" px="10px" py="4px" borderRadius="full">
                  <Icon as={PiTrendUpDuotone} boxSize="13px" />
                  <Text fontSize="12.5px" fontWeight="800">Frekwencja {turnout}%</Text>
                </HStack>
              )}
            </HStack>
            <Text fontSize={{ base: "22px", md: "27px" }} fontWeight="900" color="#1b1030" letterSpacing="-0.02em" lineHeight="1.15" noOfLines={3}>
              {voting.title || "Głosowanie"}
            </Text>
            <HStack spacing="7px" color="#6b6480" fontSize="13.5px" mt="8px" wrap="wrap">
              <Icon as={PiCalendarBlankDuotone} boxSize="14px" />
              <Text>{formatDate(voting.start_date)} — {formatDate(voting.end_date)}</Text>
            </HStack>
          </Box>
        </Flex>
      </Box>

      {/* ── KPI ── */}
      {results && (
        <Box borderRadius="22px" sx={GLASS} p={{ base: "16px", md: "20px" }}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing="12px">
            <StatTile icon={PiUsersThreeDuotone} value={results.eligible_count} label="Uprawnionych" color="#7b2ff7" />
            <StatTile icon={PiCheckCircleDuotone} value={results.voted_count} label="Oddanych głosów" color="#1f9e18" />
            <StatTile icon={PiTrendUpDuotone} value={turnout != null ? `${turnout}%` : "—"} label="Frekwencja" color="#e51f44" />
            <StatTile icon={PiListChecksDuotone} value={results.questions.length} label="Pytań" color="#2563eb" />
          </SimpleGrid>
          {turnout != null && (
            <Box mt="14px" bg="rgba(24,12,48,0.028)" borderRadius="14px" p="14px 16px">
              <Flex justify="space-between" mb="7px">
                <Text fontSize="13px" fontWeight="700" color="#6b6480">Frekwencja</Text>
                <Text fontSize="13px" fontWeight="800" color="#241536">{results.voted_count} / {results.eligible_count}</Text>
              </Flex>
              <Progress value={turnout} borderRadius="full" h="9px" sx={{ "& > div": { background: "linear-gradient(90deg, #430098, #7b2ff7)" } }} bg="rgba(67,0,152,0.08)" />
            </Box>
          )}
        </Box>
      )}

      {/* ── BLOCKCHAIN ── */}
      {id && (
        <Box borderRadius="22px" sx={GLASS} p={{ base: "16px", md: "20px" }}>
          <BlockchainInfo votingId={id} showPending bare />
        </Box>
      )}

      {/* ── ZAKŁADKI: Uprawnieni / Wyniki (pigułki) ── */}
      <Box borderRadius="24px" sx={GLASS} p={{ base: "16px", md: "20px" }}>
        <HStack spacing="4px" mb="18px" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
          {([
            { key: "uprawnieni" as ManageTab, label: `${useVoters ? "Uprawnieni" : "Zapisani"}${participants ? ` · ${useVoters ? participants.voters.length : participants.leads.length}` : ""}`, icon: PiUsersThreeDuotone },
            { key: "wyniki" as ManageTab, label: "Wyniki", icon: PiChartBarDuotone }
          ]).map(t => {
            const active = tab === t.key;
            return (
              <Button key={t.key} onClick={() => setTab(t.key)} leftIcon={<Icon as={t.icon} boxSize="15px" />} size="sm" borderRadius="full" fontWeight="800" px="15px" boxShadow="none"
                color={active ? "#7b2ff7" : "#6b6480"} bg={active ? "rgba(123,47,247,0.1)" : "transparent"}
                _hover={{ bg: active ? "rgba(123,47,247,0.16)" : "rgba(24,12,48,0.06)" }} flexShrink={0}>
                {t.label}
              </Button>
            );
          })}
        </HStack>

        {/* UPRAWNIENI / ZAPISANI */}
        {tab === "uprawnieni" && (
          <Box>
            {canAddVoters && (
              <Box mb="16px" bg="rgba(24,12,48,0.028)" borderRadius="16px" p="16px">
                <Text fontSize="14px" fontWeight="800" color="#241536" mb="9px">Dopisz uprawnionych ręcznie</Text>
                <Flex gap="8px" direction={{ base: "column", sm: "row" }}>
                  <Input value={addEmails} onChange={e => setAddEmails(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} placeholder="adresy e-mail (oddziel spacją lub przecinkiem)" h="44px" borderRadius="12px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" _hover={{ borderColor: drgba("#430098", 0.35) }} _focusVisible={{ borderColor: "primary", boxShadow: `0 0 0 3px ${drgba("#430098", 0.14)}` }} />
                  <Button onClick={handleAdd} isLoading={adding} bg="primary" color="white" borderRadius="12px" h="44px" px="22px" fontWeight="800" flexShrink={0} _hover={{ bg: "#360079" }}>Dodaj</Button>
                </Flex>
              </Box>
            )}

            <HStack spacing="10px" maxW="360px" mb="16px" bg="rgba(255,255,255,0.7)" border="1px solid rgba(24,12,48,0.08)" borderRadius="12px" px="13px" h="42px" _focusWithin={{ borderColor: drgba("#430098", 0.4), boxShadow: `0 0 0 3px ${drgba("#430098", 0.12)}` }}>
              <Icon as={PiMagnifyingGlassDuotone} boxSize="18px" color="#9a94ab" />
              <Input variant="unstyled" value={search} onChange={e => setSearch(e.target.value)} placeholder="Szukaj po e-mailu…" fontSize="14px" fontWeight="600" />
            </HStack>

            {/* Pasek postępu (oddane głosy / potwierdzone zapisy) */}
            {!isOpenElectorate && participants && (useVoters ? participants.voters.length : participants.leads.length) > 0 && (() => {
              const done = useVoters ? (participants.voted_count ?? participants.voters.filter(v => v.voted).length) : participants.leads.filter(l => l.confirmed).length;
              const total = useVoters ? participants.voters.length : participants.leads.length;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const c = useVoters ? "#1f9e18" : "#7c3aed";
              return (
                <Box bg="rgba(24,12,48,0.028)" borderRadius="16px" p="14px 16px" mb="16px">
                  <Flex justify="space-between" align="center" mb="9px">
                    <HStack spacing="8px" color="#5a5468">
                      <Icon as={useVoters ? PiCheckCircleDuotone : PiUserPlusDuotone} boxSize="16px" color={c} />
                      <Text fontSize="13px" fontWeight="800">{useVoters ? "Oddane głosy" : "Potwierdzone zapisy"}</Text>
                    </HStack>
                    <Text fontSize="14px" fontWeight="900" color="#241536">{done} <Text as="span" color="#a29bb2" fontWeight="700">z {total}</Text> · {pct}%</Text>
                  </Flex>
                  <Progress value={pct} borderRadius="full" h="8px" bg="rgba(67,0,152,0.08)" sx={{ "& > div": { background: c } }} />
                  {useVoters && !revealParticipation && (
                    <HStack spacing="6px" mt="9px" color="#8a8397"><Icon as={PiLockDuotone} boxSize="13px" /><Text fontSize="12px" lineHeight="1.4">Kto oddał głos — imiennie dopiero <strong>po zakończeniu</strong> (prywatność i odporność na presję).</Text></HStack>
                  )}
                </Box>
              );
            })()}

            {isOpenElectorate ? (
              <Flex direction="column" align="center" py="36px" gap="10px">
                <Flex w="48px" h="48px" borderRadius="14px" bg={drgba("#1f9e18", 0.1)} align="center" justify="center"><Icon as={PiGlobeDuotone} boxSize="24px" color="#1f9e18" /></Flex>
                <Text color="#241536" fontWeight="800" fontSize="15px">Referendum otwarte</Text>
                <Text color="#8a8397" fontSize="13.5px" textAlign="center" maxW="320px">Bez imiennej listy — głosuje każdy zweryfikowany.</Text>
              </Flex>
            ) : rows.length === 0 ? (
              <Text color="#6b6480" py="20px" fontSize="14px">{search ? "Brak pasujących wpisów." : useVoters ? "Brak uprawnionych." : "Jeszcze nikt się nie zapisał."}</Text>
            ) : (
              <VStack align="stretch" spacing="7px">
                {rows.map(r => {
                  const notColor = useVoters ? "#e51f44" : "#b7791f";
                  const notIcon = useVoters ? PiXCircleDuotone : PiHourglassMediumDuotone;
                  const pc = r.ok ? "#1f9e18" : notColor;
                  return (
                    <Flex key={r.email} align="center" justify="space-between" gap="10px" bg="rgba(24,12,48,0.028)" borderRadius="13px" px="12px" py="9px">
                      <HStack spacing="11px" minW={0}>
                        <Flex w="34px" h="34px" borderRadius="full" bg={drgba("#430098", 0.1)} align="center" justify="center" flexShrink={0}>
                          <Text fontSize="12px" fontWeight="800" color="primary">{emailInitials(r.email)}</Text>
                        </Flex>
                        <Text fontSize="14px" fontWeight="600" color="#3f3a4d" noOfLines={1}>{r.email}</Text>
                      </HStack>
                      {showStatus && (
                        <HStack spacing="5px" bg={drgba(pc, 0.1)} color={pc} px="9px" py="4px" borderRadius="full" flexShrink={0}>
                          <Icon as={r.ok ? PiCheckCircleDuotone : notIcon} boxSize="13px" />
                          <Text fontSize="12px" fontWeight="800">{r.ok ? r.okLabel : r.noLabel}</Text>
                        </HStack>
                      )}
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </Box>
        )}

        {/* WYNIKI */}
        {tab === "wyniki" && (
          results ? (
            <VStack align="stretch" spacing="14px">
              <Flex align="center" gap="12px" bg={results.valid ? "rgba(47,206,37,0.06)" : "rgba(229,31,68,0.05)"} border="1px solid" borderColor={results.valid ? "rgba(47,206,37,0.3)" : "rgba(229,31,68,0.25)"} borderRadius="16px" p="18px">
                <Flex w="42px" h="42px" borderRadius="12px" bg={drgba(results.valid ? "#1f9e18" : "#e51f44", 0.12)} align="center" justify="center" flexShrink={0}>
                  <Icon as={results.valid ? PiCheckCircleDuotone : PiXCircleDuotone} boxSize="22px" color={results.valid ? "#1f9e18" : "#e51f44"} />
                </Flex>
                <Box>
                  <Text fontSize="17px" fontWeight="900" color={results.valid ? "#1f9e18" : "#e51f44"} letterSpacing="-0.01em">{results.valid ? "Głosowanie ważne" : "Głosowanie nieważne"}</Text>
                  <Text fontSize="13.5px" color="#6b6480">
                    {results.turnout_percent != null ? `Frekwencja ${results.turnout_percent}%` : `Oddano ${results.voted_count} głosów`}
                    {results.quorum_percent != null ? ` · kworum ${results.quorum_percent}%` : " · bez kworum"}
                  </Text>
                </Box>
              </Flex>

              <Box>
                <Text fontSize="12px" fontWeight="800" color="#a29bb2" textTransform="uppercase" letterSpacing="0.05em" mb="11px">Werdykt per pytanie</Text>
                <VStack align="stretch" spacing="10px">
                  {results.questions.map((q, i) => {
                    const o = outcomeLabel(q.outcome);
                    const OIcon = OUTCOME_ICON[q.outcome.kind] ?? PiWarningCircleDuotone;
                    return (
                      <Flex key={q.id} align="center" justify="space-between" gap="12px" bg="rgba(24,12,48,0.028)" borderRadius="14px" borderLeft="3px solid" borderColor={o.c} p="12px 14px">
                        <HStack spacing="11px" minW={0}>
                          <Flex w="30px" h="30px" borderRadius="9px" bg={drgba(o.c, 0.12)} align="center" justify="center" flexShrink={0}><Text fontSize="13px" fontWeight="900" color={o.c}>{i + 1}</Text></Flex>
                          <Text fontWeight="700" color="#241536" fontSize="14px" noOfLines={2}>{convertHtmlToText(q.content)}</Text>
                        </HStack>
                        <HStack spacing="5px" bg={drgba(o.c, 0.1)} color={o.c} px="10px" py="4px" borderRadius="full" flexShrink={0}>
                          <Icon as={OIcon} boxSize="14px" />
                          <Text fontSize="12.5px" fontWeight="800" noOfLines={1}>{o.t}</Text>
                        </HStack>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>

              <Button as={Link} to={`/organizator/glosowanie/${id}/wyniki`} leftIcon={<Icon as={PiChartBarDuotone} />} alignSelf="flex-start" bg="primary" color="white" borderRadius="12px" fontWeight="800" _hover={{ bg: "#360079", textDecoration: "none" }}>
                Pełne wyniki
              </Button>
            </VStack>
          ) : (
            <Text color="#6b6480" py="20px" fontSize="14px">Wyniki będą dostępne, gdy pojawią się głosy.</Text>
          )
        )}
      </Box>
    </VStack>
  );

  return (
    <Flex align="flex-start">
      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "24px", md: "36px" }}>
          {loading ? (
            <Flex justify="center" py="100px"><Spinner color="primary" size="lg" thickness="3px" /></Flex>
          ) : !voting ? (
            <Flex direction="column" align="center" justify="center" py="70px" borderRadius="22px" sx={GLASS}>
              <Flex w="58px" h="58px" borderRadius="16px" bg={drgba("#430098", 0.1)} align="center" justify="center" mb="14px"><Icon as={PiListChecksDuotone} boxSize="28px" color="primary" /></Flex>
              <Text color="#241536" fontWeight="900" fontSize="18px">Nie udało się wczytać głosowania</Text>
            </Flex>
          ) : (
            <>
              {main}
              {/* Rail zwinięty <xl */}
              <Box display={{ base: "block", xl: "none" }} mt="18px" borderRadius="22px" px="18px" py="20px" sx={GLASS}>{rail}</Box>
            </>
          )}
        </Box>
      </Box>

      {/* ── GŁÓWNY PRAWY RAIL — pływający, pełnowysokościowy ── */}
      {!loading && voting && (
        <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
          <Box position="sticky" top="0" h="100vh" p="12px">
            <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
              <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 20%, rgba(124,58,237,0.14), transparent 72%)" }} />
              <VStack position="relative" align="stretch" spacing="0" h="100%" px="18px" py="22px" sx={{ ...SCROLL_FOG, maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)" }}>
                {rail}
              </VStack>
            </Box>
          </Box>
        </Box>
      )}
    </Flex>
  );
};

export default VotingManageView;
